import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export interface AuthUser {
  id: number;
  email: string;
  role: 'admin' | 'user';
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>(null!);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('ensight_token'));
  const [user, setUser] = useState<AuthUser | null>(() => {
    const saved = localStorage.getItem('ensight_user');
    try { return saved ? JSON.parse(saved) : null; } catch { return null; }
  });

  // Token aus URL-Parameter übernehmen (von Mobile App übergeben)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (!urlToken) return;
    const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
    fetch(`${base}/auth/me`, { headers: { Authorization: `Bearer ${urlToken}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((u: AuthUser) => {
        setToken(urlToken);
        setUser(u);
        localStorage.setItem('ensight_token', urlToken);
        localStorage.setItem('ensight_user', JSON.stringify(u));
        // Token aus URL entfernen
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        window.history.replaceState({}, '', url.toString());
      })
      .catch(() => {});
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const form = new FormData();
    form.append('email', email);
    form.append('password', password);
    const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
    const res = await fetch(`${base}/auth/login`, { method: 'POST', body: form });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Login fehlgeschlagen');
    }
    const data = await res.json();
    const u: AuthUser = { id: data.id ?? 0, email: data.email, role: data.role };
    setToken(data.access_token);
    setUser(u);
    localStorage.setItem('ensight_token', data.access_token);
    localStorage.setItem('ensight_user', JSON.stringify(u));
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('ensight_token');
    localStorage.removeItem('ensight_user');
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAdmin: user?.role === 'admin' }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
